import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useCartStore } from "@/stores/cart";

const SYNC_DEBOUNCE_MS = 2_000;
// Server carts older than 24 hours are discarded on merge — prevents cross-session inflation
const SERVER_CART_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface SavedCartRow {
  id: string;
  user_id: string;
  items: Array<{ snapshot: Record<string, unknown>; quantity: number }>;
  updated_at: string;
}

/**
 * Syncs the cart to Supabase for logged-in users.
 *
 * - On SIGNED_IN: fetches the server cart, merges with local (higher quantity wins),
 *   pushes merged result to server, updates local store.
 * - On every local cart mutation (debounced 2s): upserts to saved_carts.
 * - On SIGNED_OUT: local cart is cleared by use-cart-auth (not here).
 *   Server cart is preserved for future re-login.
 */
export function useCartSync() {
  const persistedItems = useCartStore((s) => s._persistedItems);
  const _replaceItems = useCartStore((s) => s._replaceItems);
  const _getPersistedItems = useCartStore((s) => s._getPersistedItems);
  const userIdRef = useRef<string | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMergingRef = useRef(false);

  // ── Push local cart to server ──────────────────────────────────────────
  const pushToServer = useCallback(
    async (userId: string) => {
      const items = _getPersistedItems();
      if (items.length === 0) {
        // Delete server cart if local is empty
        await supabase.from("saved_carts").delete().eq("user_id", userId);
        return;
      }

      await supabase.from("saved_carts").upsert(
        {
          user_id: userId,
          items: items as unknown as SavedCartRow["items"],
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" },
      );
    },
    [_getPersistedItems],
  );

  // ── Merge server cart with local cart ───────────────────────────────────
  const mergeOnLogin = useCallback(
    async (userId: string) => {
      isMergingRef.current = true;

      try {
        const { data: serverCart } = await supabase
          .from("saved_carts")
          .select("items, updated_at")
          .eq("user_id", userId)
          .single<Pick<SavedCartRow, "items" | "updated_at">>();

        const localItems = _getPersistedItems();

        // Discard server cart if it is older than 24 hours — prevents stale
        // items from inflating the cart across browser sessions
        const serverCartAge = serverCart?.updated_at
          ? Date.now() - new Date(serverCart.updated_at).getTime()
          : Infinity;
        const serverItems =
          serverCart && serverCartAge <= SERVER_CART_MAX_AGE_MS
            ? ((serverCart.items ?? []) as unknown as typeof localItems)
            : [];

        if (serverItems.length === 0) {
          // No server cart (or expired) — just push local to server
          if (localItems.length > 0) {
            await pushToServer(userId);
          }
          return;
        }

        if (localItems.length === 0) {
          // No local cart — use server cart
          _replaceItems([...serverItems]);
          toast.info("Panier récupéré", {
            description: `${serverItems.length} article(s) de votre précédente session.`,
          });
          return;
        }

        // Merge: for each product, keep the higher quantity
        const merged = new Map<
          string,
          (typeof localItems)[0]
        >();

        for (const item of serverItems) {
          if (item?.snapshot?.id) {
            merged.set(item.snapshot.id, item);
          }
        }

        for (const item of localItems) {
          if (!item?.snapshot?.id) continue;
          const existing = merged.get(item.snapshot.id);
          if (existing) {
            // Keep higher quantity, prefer local snapshot (fresher)
            merged.set(item.snapshot.id, {
              snapshot: item.snapshot,
              quantity: Math.max(item.quantity, existing.quantity),
            });
          } else {
            merged.set(item.snapshot.id, item);
          }
        }

        const mergedArray = Array.from(merged.values());
        _replaceItems(mergedArray);
        await pushToServer(userId);

        const recoveredCount = serverItems.filter(
          (si) =>
            si?.snapshot?.id &&
            !localItems.some((li) => li.snapshot.id === si.snapshot.id),
        ).length;

        if (recoveredCount > 0) {
          toast.info("Panier fusionné", {
            description: `${recoveredCount} article(s) récupéré(s) de votre précédente session.`,
          });
        }
      } finally {
        isMergingRef.current = false;
      }
    },
    [_getPersistedItems, _replaceItems, pushToServer],
  );

  // ── Auth listener ──────────────────────────────────────────────────────
  useEffect(() => {
    // Check current session on mount
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        userIdRef.current = user.id;
        mergeOnLogin(user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        userIdRef.current = session.user.id;
        mergeOnLogin(session.user.id);
      } else if (event === "SIGNED_OUT") {
        userIdRef.current = null;
      }
    });

    return () => subscription.unsubscribe();
  }, [mergeOnLogin]);

  // ── Debounced sync on cart changes ─────────────────────────────────────
  useEffect(() => {
    if (!userIdRef.current) return;
    if (isMergingRef.current) return;

    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }

    const userId = userIdRef.current;
    syncTimerRef.current = setTimeout(() => {
      pushToServer(userId);
    }, SYNC_DEBOUNCE_MS);

    return () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, [persistedItems, pushToServer]);
}
